import React from 'react';
import * as S from './styled';
import { ButtonProps as StyledButtonProps } from './styled'

type IconProp = string | React.ComponentType<any> | React.ReactElement

interface ButtonProps extends Omit<StyledButtonProps, 'icon'> {
  icon?: IconProp;
  iconSize?: number;
  iconColor?: string;
}

type Props = React.ComponentProps<'button'> &
  React.ComponentProps<'a'> &
  ButtonProps;

/**
 * Button
 * @version 2.0.0
 * @author [Ubayd Rahmonzoda](ubayd.rahmonzoda@gmail.com)
 */
const Button = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    isDisabled = false,
    isSelected = false,
    isLoading = false,
    children = null,
    className = '',
    inline = false,
    type = 'button',
    icon,
    iconSize = 14,
    iconColor = 'inherit',
    spacing = 'default',
    appearance = 'default',
    onClick = () => { },
    ...restProps
  } = props;

  const renderIcon = () => {
    if (!icon) return null;

    if (React.isValidElement(icon)) {
      return icon;
    }

    if (typeof icon === 'function') {
      const IconComponent = icon;
      return <IconComponent />;
    }

    return null;
  }

  return (
    <S.Button
      {...restProps}
      ref={ref}
      type={type}
      spacing={spacing}
      className={className}
      appearance={appearance}
      onClick={onClick}
      inline={inline}
      isSelected={isSelected}
      disabled={isDisabled}
    >
      {renderIcon()}
      {children && <span>{children}</span>}
      {isLoading && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width={iconSize}
          height={iconSize}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="spin"
        >
          <path d="M12 2v4" />
          <path d="m16.2 7.8 2.9-2.9" />
          <path d="M18 12h4" />
          <path d="m16.2 16.2 2.9 2.9" />
          <path d="M12 18v4" />
          <path d="m4.9 19.1 2.9-2.9" />
          <path d="M2 12h4" />
          <path d="m4.9 4.9 2.9 2.9" />
        </svg>
      )}
    </S.Button>
  );
});

Button.displayName = 'Button';

export default Button;
